// src/pages/Connector/Salesforce/ListSalesforceLeads.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import { Tooltip, Modal as MuiModal, Box } from '@mui/material';
import CreateEditLeadModal from "./CreateEditLeadModal";

export default function ListSalesforceLeads({ refreshTrigger, permission }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchLeads = useCallback(async (page = currentPage, pageSize = perPage, search = searchText) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axiosInstance.get(`/api/salesforce/leads`, {
                params: {
                    page: page,
                    per_page: pageSize,
                    search: search
                }
            });
            
            if (response.data.success) {
                setLeads(response.data.data);
                setTotalRows(response.data.pagination.total);
            } else {
                setError(response.data.message || "Failed to fetch leads");
            }
        } catch (err) {
            console.error("Error fetching Salesforce leads:", err);
            setError(err.response?.data?.message || "Error fetching leads.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, searchText]);

    useEffect(() => {
        fetchLeads();
    }, [refreshTrigger]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchText !== undefined) {
                setCurrentPage(1);
                fetchLeads(1, perPage, searchText);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchText]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchLeads(page, perPage, searchText);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchLeads(page, newPerPage, searchText);
    };

    const handleCreateLead = () => {
        setCurrentLead(null);
        setIsCreateModalOpen(true);
    };

    const handleEditLead = async (leadId) => {
        try {
            const response = await axiosInstance.get(`/api/salesforce/leads/${leadId}`);
            if (response.data.success) {
                setCurrentLead(response.data.data);
                setIsEditModalOpen(true);
            }
        } catch (err) {
            setError("Failed to fetch lead details");
        }
    };

    const handleDeleteLead = async (leadId, leadName) => {
        if (!window.confirm(`Are you sure you want to delete lead "${leadName}"? This action cannot be undone.`)) {
            return;
        }

        setIsDeleting(true);
        setError(null);
        
        try {
            await axiosInstance.delete(`/api/salesforce/leads/${leadId}`);
            setError("Lead deleted successfully.");
            fetchLeads();
        } catch (err) {
            console.error(`Error deleting lead ${leadId}:`, err);
            setError(err.response?.data?.message || `Error deleting lead.`);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleModalClose = () => {
        setIsCreateModalOpen(false);
        setIsEditModalOpen(false);
        setCurrentLead(null);
    };

    const handleModalSuccess = () => {
        handleModalClose();
        fetchLeads();
        setError("Operation completed successfully.");
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => `${row.FirstName || ''} ${row.LastName || ''}`.trim(),
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: "Company",
            selector: (row) => row.Company,
            sortable: true,
            wrap: true
        },
        {
            name: "Email",
            selector: (row) => row.Email,
            sortable: true,
            wrap: true
        },
        {
            name: "Phone",
            selector: (row) => row.Phone,
            sortable: true
        },
        {
            name: "Status",
            selector: (row) => row.Status,
            sortable: true,
            cell: (row) => (
                <span className={`badge ${getStatusBadgeClass(row.Status)}`}>
                    {row.Status}
                </span>
            )
        },
        {
            name: "Actions",
            width: '150px',
            cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
                    {permission.canModify && (
                        <Tooltip title="Edit Lead" arrow>
                            <button 
                                className="action-icon" 
                                onClick={() => handleEditLead(row.Id)}
                                disabled={loading || isDeleting}
                            >
                                <i className="fa fa-pencil"></i>
                            </button>
                        </Tooltip>
                    )}
                    {permission.canDelete && (
                        <Tooltip title="Delete Lead" arrow>
                            <button 
                                className="action-icon delete" 
                                onClick={() => handleDeleteLead(row.Id, `${row.FirstName || ''} ${row.LastName || ''}`)}
                                disabled={loading || isDeleting}
                            >
                                <i className="fa fa-trash-o"></i>
                            </button>
                        </Tooltip>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const getStatusBadgeClass = (status) => {
        const statusMap = {
            'Open - Not Contacted': 'bg-primary',
            'Working - Contacted': 'bg-info',
            'Closed - Converted': 'bg-success',
            'Closed - Not Converted': 'bg-secondary'
        };
        return statusMap[status] || 'bg-secondary';
    };

    const ExpandedComponent = ({ data }) => (
        <div style={{ padding: '15px', borderLeft: '3px solid #0d6efd', backgroundColor: '#f8f9fa', fontSize: '0.9em' }}>
            <p><strong>Lead ID:</strong> {data.Id}</p>
            <p><strong>Name:</strong> {`${data.FirstName || ''} ${data.LastName || ''}`.trim()}</p>
            <p><strong>Company:</strong> {data.Company || 'N/A'}</p>
            <p><strong>Email:</strong> {data.Email || 'N/A'}</p>
            <p><strong>Phone:</strong> {data.Phone || 'N/A'}</p>
            <p><strong>Status:</strong> {data.Status || 'N/A'}</p>
            <p><strong>Created:</strong> {data.CreatedDate ? new Date(data.CreatedDate).toLocaleString() : 'N/A'}</p>
            <p><strong>Last Modified:</strong> {data.LastModifiedDate ? new Date(data.LastModifiedDate).toLocaleString() : 'N/A'}</p>
        </div>
    );

    return (
        <div className="mt-3">
            <div className="row mb-3">
                <div className="col-md-6">
                    {permission.canModify && (
                        <button
                            className="btn btn-primary"
                            onClick={handleCreateLead}
                            disabled={loading}
                        >
                            <i className="fa fa-plus me-2"></i>Create New Lead
                        </button>
                    )}
                </div>
                <div className="col-md-6">
                    <input
                        type="text"
                        placeholder="Search by name, company, or email..."
                        className="form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            {error && (
                <div className={`alert ${error.toLowerCase().includes("error") || error.toLowerCase().includes("failed") ? "alert-danger" : "alert-success"} my-2 alert-dismissible fade show`} role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                </div>
            )}

            <DataTable
                columns={columns}
                data={leads}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                responsive
                highlightOnHover
                pointerOnHover
                expandableRows
                expandableRowsComponent={ExpandedComponent}
            />

            {/* Create Lead Modal */}
            {isCreateModalOpen && (
                <CreateEditLeadModal
                    isOpen={isCreateModalOpen}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                    lead={null}
                    mode="create"
                    permission={permission}
                />
            )}

            {/* Edit Lead Modal */}
            {isEditModalOpen && currentLead && (
                <CreateEditLeadModal
                    isOpen={isEditModalOpen}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                    lead={currentLead}
                    mode="edit"
                    permission={permission}
                />
            )}

            <style jsx global>{`
                .action-icon { 
                    background: none; 
                    border: none; 
                    padding: 5px; 
                    cursor: pointer; 
                    color: #0d6efd; 
                    font-size: 1.1em; 
                }
                .action-icon.delete { color: #dc3545; }
                .action-icon:hover { opacity: 0.7; }
                .action-icon:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
        </div>
    );
}